/**
 * Pure, framework-free rules for deciding whether a finished audio recording is long enough to upload.
 *
 * This logic lives outside the (very large) AudioRecorder component on purpose: the original "Recording Too Short" bug happened because the duration check was tangled inside a closure and read a stale React state value. Keeping the rule here lets us unit-test the invariant directly, with no browser/React mocks.
 */

/**
 * Minimum recording length, in whole seconds, required before an upload is allowed. The recording timer ticks in whole seconds, so a value of 3 effectively enforces a ~2.5s+ real-world minimum.
 */
export const MINIMUM_STOP_SECONDS = 3;

export interface RecordingStopDecision {
  /** True when the recording is below the minimum length. */
  isTooShort: boolean;
  /** True when the recording may be uploaded (i.e. it is not too short). */
  canUpload: boolean;
}

/**
 * Returns whether a recording of `elapsedSeconds` is too short to upload.
 *
 * Non-finite or negative durations are treated as "too short" so that an unknown/uninitialized duration fails safe rather than allowing an empty upload. Callers should pass the live elapsed value (e.g. a ref's current value), never a potentially stale captured snapshot.
 */
export function isRecordingTooShort(
  elapsedSeconds: number,
  minimumSeconds: number = MINIMUM_STOP_SECONDS
): boolean {
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 0) {
    return true;
  }
  return elapsedSeconds < minimumSeconds;
}

/**
 * Convenience wrapper returning both the "too short" and "can upload" flags for a given elapsed duration.
 */
export function evaluateRecordingStop(
  elapsedSeconds: number,
  minimumSeconds: number = MINIMUM_STOP_SECONDS
): RecordingStopDecision {
  const tooShort = isRecordingTooShort(elapsedSeconds, minimumSeconds);
  return { isTooShort: tooShort, canUpload: !tooShort };
}
